'use client';

import { FC, PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { addEquipmentOnSubmit } from 'services/equipment/actions';
import { useQuickToast } from 'hooks/use-toast';
import { useTranslations } from 'next-intl';
import { RegisterSignInButtonToast } from 'components/my-equipment/equipment-toast/register-signin-button-toast';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import {
  DUPLICATE_EQUIPMENT_ERROR,
  FORM_FIELD_NICK_NAME,
  GENERIC_ERROR,
  NOTIFY_DEALER,
  SELECTED_EQUIPMENT
} from 'components/my-equipment/constants';
import type { Equipment } from 'services/equipment-search/types';
import { useFeedback } from 'providers/feedback-provider';

const AddEquipmentFormWrapper: FC<
  PropsWithChildren<{
    isLoggedIn?: boolean;
  }>
> = ({ children, isLoggedIn }) => {
  const { updateFeedback, dismissFeedback } = useFeedback();
  const t = useTranslations();
  const UNKNOWN_ERROR_MESSAGE = t('UNKNOWN_ERROR_MESSAGE');

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      serial: '',
      nickName: '',
      model: '',
      selectedEquipment: null,
      notifyDealer: false
    }
  });
  const { getValues, reset, handleSubmit } = formMethods;
  const toast = useQuickToast();

  const onSubmit = async () => {
    try {
      const state = await addEquipmentOnSubmit(
        getValues(SELECTED_EQUIPMENT) ?? {},
        getValues(NOTIFY_DEALER) ?? false,
        getValues(FORM_FIELD_NICK_NAME) ?? ''
      );

      if (!state) return;
      if (state.status === 'success') {
        showAddEquipmentToast(getValues(SELECTED_EQUIPMENT));
        reset();
        dismissFeedback();
      }
      // this is to show duplicate Banner
      if (state.status === 'error' && 'type' in state.data) {
        if (state.data.type === DUPLICATE_EQUIPMENT_ERROR) {
          updateFeedback(DUPLICATE_EQUIPMENT_ERROR);
        } else if (state.data.type === GENERIC_ERROR) {
          updateFeedback(UNKNOWN_ERROR_MESSAGE);
        }
      }
    } catch (error) {
      updateFeedback(UNKNOWN_ERROR_MESSAGE);
    }
  };

  const showAddEquipmentToast = (selectedEquipmentValues: Equipment) => {
    const currentlyShoppingText = t('CURRENTLY_SHOPPING', {
      0: selectedEquipmentValues?.model,
      1: capitalizeText(selectedEquipmentValues?.equipmentFamily)
    });

    const contentGuestToast = !isLoggedIn ? (
      <RegisterSignInButtonToast
        signInText={t('SIGN_IN')}
        registerText={t('SL_REGISTER')}
        guestSaveText={t('GUEST_EQUIPMENT_SAVE')}
      />
    ) : null;

    toast.success({
      message: currentlyShoppingText,
      content: contentGuestToast,
      delay: 5
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>{children}</form>
    </FormProvider>
  );
};

export default AddEquipmentFormWrapper;
